import "lazysizes";
import {
  configureLazySizes,
  initLazyMedia,
  initFeaturedCoverMedia,
} from "./utils/lazy-media";

import Swup from "swup";
import SwupScrollPlugin from "@swup/scroll-plugin";
import SwupBodyClassPlugin from "@swup/body-class-plugin";
import List from "list.js";

import { toggleView } from "./components/toggle-view";
import { initProjectsView } from "./components/projects-view";
import { collapsibleText } from "./components/collapsible-text";
import { floatingImages } from "./components/floating-images";
import { initMaLogoScroll, syncHomeIntroClasses } from "./components/ma-logo-scroll";
import { initColorZones } from "./components/color-zones";
import Lightbox from "./components/lightbox";
import ProjectModal from "./components/project-modal";
import { initNewsAccordion } from "./components/news-accordion";
import { initNewsLoadMore, destroyNewsLoadMore } from "./components/news-load-more";
import { initNewsYears, destroyNewsYears } from "./components/news-years";
import { initCarousel } from "./components/carousel";
import { initHomeSlideshow } from "./components/home-slideshow";
import { initAboutCarousel } from "./components/about-carousel";
import { initAboutTeam } from "./components/about-team";
import { initAboutNav, destroyAboutNav } from "./components/about-nav";
import { applyReducedMotionVideos, prefersReducedMotion } from "./utils/reduced-motion";
import { projectsIndexUrl, newsIndexUrl } from "./utils/language-url";
import { initLangSwitcher } from "./components/lang-switcher";
import { initHeaderContact, closeHeaderContact } from "./components/header-contact";
import { initMobileNavMenu, refreshMobileNav, closeMobileNav } from "./components/mobile-nav";
import { initSal, teardownSal } from "./components/sal-init";

console.log("Modulo Architects. Design & Development by Matthias Deckx (https://matthiasdeckx.studio)");

configureLazySizes();

window.main = window.main || {};

function updateDocumentMeta(doc) {
  if (!doc) return;

  const title = doc.querySelector("title");
  if (title) {
    document.title = title.textContent;
  }

  const description = doc.querySelector('meta[name="description"]');
  if (description) {
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", description.getAttribute("content") || "");
  }

  const canonical = doc.querySelector('link[rel="canonical"]');
  if (canonical) {
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", canonical.getAttribute("href") || "");
  }

  const nextBody = doc.querySelector("body");
  if (nextBody) {
    ["projectsUrl", "newsUrl"].forEach((key) => {
      if (nextBody.dataset[key]) {
        document.body.dataset[key] = nextBody.dataset[key];
      }
    });

    // Keep page color-combination CSS vars in sync (Swup only replaces #main).
    const nextStyle = nextBody.getAttribute("style");
    if (nextStyle) {
      document.body.setAttribute("style", nextStyle);
    } else {
      document.body.removeAttribute("style");
    }
  }

  // Header stays outside Swup containers — keep language switcher in sync
  const nextLangs = doc.querySelector(".c-primary-nav__langs");
  const currentLangs = document.querySelector(".c-primary-nav__langs");
  if (nextLangs && currentLangs) {
    currentLangs.replaceWith(nextLangs.cloneNode(true));
  }
}

let isHomepage = false;
let isProjectPage = false;
let isProjectsPage = false;
let isNewsPage = false;
let isArticlePage = false;
let isAboutPage = false;
let isTouchDevice = false;
let isNavToggled = false;
let introRan = false;
let projectModal = null;

function checks() {
  function checkIfTouchDevice() {
    return (
      true ==
      ("ontouchstart" in window ||
        (window.DocumentTouch && document instanceof DocumentTouch))
    );
  }

  if (checkIfTouchDevice() === true) {
    document.body.classList.add("is-touch");
    document.body.classList.add("is-mobile-device");
    isTouchDevice = true;
  } else {
    isTouchDevice = false;
  }

  if (document.body.classList.contains("page-home")) {
    isHomepage = true;
  } else {
    isHomepage = false;
  }

  if (document.body.classList.contains("page-project")) {
    isProjectPage = true;
  } else {
    isProjectPage = false;
  }

  if (document.body.classList.contains("page-projects")) {
    isProjectsPage = true;
  } else {
    isProjectsPage = false;
  }

  if (document.body.classList.contains("page-news")) {
    isNewsPage = true;
  } else {
    isNewsPage = false;
  }

  if (document.body.classList.contains("page-article")) {
    isArticlePage = true;
  } else {
    isArticlePage = false;
  }

  if (document.body.classList.contains("page-about")) {
    isAboutPage = true;
  } else {
    isAboutPage = false;
  }

  updateSiteHeader();
  initColorZones();
  syncHomeIntroClasses();
  swupActiveLinks();
}

function updateBrandHomeLink() {
  const brandItem = document.querySelector(".c-primary-nav__item--brand");
  if (!brandItem) {
    return;
  }

  const current = brandItem.querySelector(".c-primary-nav__link");
  if (!current) {
    return;
  }

  const homeUrl = document.body.dataset.homeUrl || "/";
  const isHome = document.body.classList.contains("page-home");
  const label = current.textContent;

  if (isHome) {
    if (current.tagName === "A") {
      const span = document.createElement("span");
      span.className = "c-primary-nav__link is-active";
      span.setAttribute("aria-current", "page");
      span.textContent = label;
      current.replaceWith(span);
    } else {
      current.classList.add("is-active");
      current.setAttribute("aria-current", "page");
    }
    return;
  }

  if (current.tagName !== "A") {
    const link = document.createElement("a");
    link.href = homeUrl;
    link.className = "c-primary-nav__link";
    link.textContent = label;
    current.replaceWith(link);
    return;
  }

  current.setAttribute("href", homeUrl);
  current.classList.remove("is-active");
  current.removeAttribute("aria-current");
}

/**
 * Swup only replaces #main. If the user hard-loaded a project page that omitted
 * brand/nav (legacy markup) or left brand [hidden], restore from the destination
 * document so Close / home / back leave a usable site header.
 */
function syncSiteHeaderFromDocument(doc) {
  if (!doc) {
    return;
  }

  const currentHeader = document.getElementById("header");
  const nextHeader = doc.getElementById("header");
  if (!currentHeader || !nextHeader) {
    return;
  }

  const currentBrand = currentHeader.querySelector(".c-site-header__brand");
  const nextBrand = nextHeader.querySelector(".c-site-header__brand");

  if (!currentBrand && nextBrand) {
    currentHeader.innerHTML = nextHeader.innerHTML;
  }
}

function updateSiteHeader() {
  const header = document.getElementById("header");
  if (!header) {
    return;
  }

  const brand = header.querySelector(".c-site-header__brand");

  header.classList.remove(
    "c-site-header--overlay",
    "c-site-header--default",
    "c-site-header--about"
  );

  if (document.body.classList.contains("page-home")) {
    header.classList.add("c-site-header--overlay");
  } else if (document.body.classList.contains("page-about")) {
    header.classList.add("c-site-header--about");
  } else {
    header.classList.add("c-site-header--default");
  }

  // Full project pages use the normal site header (brand visible).
  // Overlay modal has its own chrome brand.
  if (brand) {
    brand.hidden = false;
  }

  updateBrandHomeLink();
}

function initSortableList() {
  const projectsList = document.getElementById("projects-list");
  if (projectsList) {
    const options = { valueNames: ["title", "typology", "client", "year"] };
    new List("projects-list", options);
  }
}

function setupPlugins() {
  function initSwup() {
    const swup = new Swup({
      containers: ["#main"],
      animateHistoryBrowsing: true,
      animationSelector: '[class*="transition-fade"]',
      cache: true, // Enable caching for faster transitions
      // Do not set skipPopStateHandling to a boolean — Swup calls it as a
      // function on popstate. `false` throws and leaves URL updated with stale
      // #main (homepage slideshow never re-inits on browser back).
      linkSelector: 'a[href]:not([data-no-swup]):not(.c-home-slideshow__link):not(.c-projects-list__item-link):not(.c-projects-selected__link):not(.c-projects-index__link):not(.c-projects-index-table__link)',
      plugins: [
        new SwupScrollPlugin({
          animateScroll: false,
        }),
        new SwupBodyClassPlugin({
          prefix: "page-",
          // Per-page combo colors live on body style (--color-combo-*).
          attributes: ["style"],
        }),
      ],
    });

    // Store swup instance globally for debugging
    window.swup = swup;

    // Swup 4.0 uses hooks API
    if (swup.hooks) {
      const setPageTransitioning = (active) => {
        document.body.classList.toggle("is-page-transitioning", active);
      };

      swup.hooks.on("visit:start", (visit) => {
        if (prefersReducedMotion()) {
          visit.animation.animate = false;
        }
        closeHeaderContact();
        teardownSal();

        // Lightbox nodes live outside #main and survive Swup; tear them down so
        // a leftover .is-active gallery cannot block clicks on the next page.
        document.querySelectorAll(".js-gallery").forEach((node) => {
          node.classList.remove("is-active");
          node.setAttribute("aria-hidden", "true");
          node.remove();
        });
        document.body.style.overflow = "";

        if (projectModal?.isOpen) {
          projectModal.close();
        }

        // Stop slideshow timers before #main is replaced (body still page-home)
        if (
          document.body.classList.contains("page-home") &&
          typeof window.main?.homeSlideshowCleanup === "function"
        ) {
          window.main.homeSlideshowCleanup();
        }
      });

      swup.hooks.on("animation:out:start", () => setPageTransitioning(true));
      swup.hooks.on("visit:end", () => {
        setPageTransitioning(false);
        // Home + SAL after enter fade: page:view runs while #main is still
        // opacity 0, so early initSal() would finish reveals invisibly.
        requestAnimationFrame(() => {
          initHomepageExperience();
          initSal();
        });
      });
      swup.hooks.on("visit:abort", () => setPageTransitioning(false));

      // Run immediately when content is replaced (during transition)
      swup.hooks.on("content:replace", (visit) => {
        // Only do critical updates immediately
        document.body.setAttribute("data-menu-open", "false");
        document.body.classList.remove("no-scroll", "is-mobile-nav-open");
        isNavToggled = false;
        closeMobileNav();
        if (visit?.to?.document) {
          syncSiteHeaderFromDocument(visit.to.document);
          updateDocumentMeta(visit.to.document);
        }
        checks(); // Update page type flags + show/hide header brand
        swupActiveLinks();
        applyReducedMotionVideos(document.getElementById("main"));
      });
      
      // Run after page transition completes (defer heavy operations)
      swup.hooks.on("page:view", () => {
        // Defer heavy initialization to avoid blocking transition
        requestAnimationFrame(() => {
          swupReload();
          swupActiveLinks(); // Set aria-current attributes first
          initMobileNav(); // Reinitialize to attach event listeners to new links
          refreshMobileNav();
          
          // Update label - use multiple attempts to ensure it works
          updateMobileNavLabel();
          setTimeout(() => {
            updateMobileNavLabel();
          }, 100);
          setTimeout(() => {
            updateMobileNavLabel();
          }, 300);
          
          // Check if we should open a project modal after navigation
          const projectUrlToOpen = sessionStorage.getItem('openProjectModal');
          if (projectUrlToOpen) {
            sessionStorage.removeItem('openProjectModal');
            setTimeout(() => {
              openStoredProjectModal(projectUrlToOpen);
            }, 300);
          }

        });
      });
    } else {
      console.error("Swup hooks API not available");
    }
  }

  initSwup();
}

function normalizeNavPath(pathname) {
  if (!pathname) {
    return "/";
  }

  const normalized = pathname.replace(/\/+$/, "") || "/";
  return normalized;
}

function isHomeNavPath(path) {
  return path === "/" || path === "/home" || /^\/[a-z]{2}$/.test(path);
}

function isPrimaryNavLinkActive(linkPath, currentPath) {
  linkPath = normalizeNavPath(linkPath);
  currentPath = normalizeNavPath(currentPath);

  if (isHomeNavPath(linkPath)) {
    return (
      currentPath === linkPath ||
      (linkPath === "/" && currentPath === "/home") ||
      (linkPath === "/home" && currentPath === "/")
    );
  }

  return currentPath === linkPath || currentPath.startsWith(`${linkPath}/`);
}

/**
 * Homepage-only inits (slideshow, logo rest, featured covers).
 * Runs on first load (init) and after Swup visit:end — not on page:view.
 */
function initHomepageExperience() {
  if (!document.querySelector("[data-home-slideshow]")) {
    return;
  }

  checks();
  syncHomeIntroClasses();
  initFeaturedCoverMedia(document.getElementById("main"));
  initMaLogoScroll();
  initHomeSlideshow();

  if (!projectModal) {
    projectModal = new ProjectModal();
  }
  initProjectLinks();
}

function swupActiveLinks() {
  const currentPath = window.location.pathname;
  const links = document.querySelectorAll(
    ".c-primary-nav__link[href]:not(.c-primary-nav__link--lang), .c-primary-nav__panel-link[href], .c-nav-mobile__link"
  );

  for (const link of links) {
    const href = link.getAttribute("href");

    if (
      !href ||
      href.startsWith("mailto:") ||
      link.classList.contains("c-primary-nav__link--contact") ||
      link.classList.contains("c-primary-nav__panel-link--contact") ||
      link.hasAttribute("data-projects-view") && link.getAttribute("data-projects-view") === "index"
    ) {
      continue;
    }

    try {
      const linkPath = new URL(link.href, window.location.origin).pathname;
      const isActive = isPrimaryNavLinkActive(linkPath, currentPath);

      link.classList.toggle("is-active", isActive);

      if (isActive) {
        link.setAttribute("aria-current", "page");
      } else {
        link.removeAttribute("aria-current");
      }
    } catch (e) {
      link.classList.remove("is-active");
      link.removeAttribute("aria-current");
    }
  }
}

function swupReload() {
  // Body class (page-home, etc.) is updated after content:replace — refresh flags here.
  checks();

  destroyNewsLoadMore();
  destroyNewsYears();
  destroyAboutNav();

  // Tear down slideshow when the hero is no longer in the DOM
  if (
    !document.querySelector("[data-home-slideshow]") &&
    typeof window.main?.homeSlideshowCleanup === "function"
  ) {
    window.main.homeSlideshowCleanup();
  }

  if (document.querySelector("[data-home-slideshow]")) {
    // Slideshow, logo, and featured covers init on visit:end (after fade-in)
  } else {
    initLazyMedia(document.getElementById("main"));
  }

  initSortableList();
  toggleView();
  initProjectsView();
  collapsibleText();
  initFilters();
  initCarousel();
  applyReducedMotionVideos(document.getElementById("main"));
  if (!document.querySelector("[data-home-slideshow]")) {
    initMaLogoScroll();
  }
  // Do not initSal here — visit:end owns scroll reveals after the enter fade.
  // Early init while #main is opacity:0 marks rows as animated with no visible transition.

  if (!isTouchDevice) {
    floatingImages();
  }

  if (isProjectPage) {
    const lb = new Lightbox({
      selector: '#main a[data-rel="aiLightbox"]', // exclude modal (own Lightbox)
      lazyload: true, // boolean
      arrows: true, // boolean
      counter: true, // boolean
      slideSpeed: 500, //number(ms)
    });
  }

  if (isNewsPage) {
    initNewsAccordion();
    initNewsLoadMore();
    initNewsYears();
  }

  if (isProjectsPage) {
    if (!projectModal) {
      projectModal = new ProjectModal();
    }
    initProjectLinks();
  }

  if (isAboutPage) {
    initAboutCarousel();
    initAboutTeam();
    initAboutNav();
  }

}

function readStoredProjectModalColor() {
  const colorRaw = sessionStorage.getItem("openProjectModalColor");
  sessionStorage.removeItem("openProjectModalColor");

  if (!colorRaw) {
    return null;
  }

  try {
    return JSON.parse(colorRaw);
  } catch (error) {
    return null;
  }
}

function storeProjectModalColorFromPage() {
  const root = document.querySelector(
    ".c-project-page[data-color-combination], .c-project-modal[data-color-combination]"
  );
  if (!root) {
    return;
  }

  sessionStorage.setItem(
    "openProjectModalColor",
    JSON.stringify({
      key: root.getAttribute("data-color-combination") || "black-white",
      accent: root.style.getPropertyValue("--project-accent").trim(),
      accentText: root.style.getPropertyValue("--project-accent-text").trim(),
    })
  );
}

function openStoredProjectModal(url) {
  if (!url) {
    return;
  }

  if (!projectModal) {
    projectModal = new ProjectModal();
  }

  projectModal.open(url, null, readStoredProjectModalColor());
}

function initProjectLinks() {
  // Initialize modal if it doesn't exist
  if (!projectModal) {
    projectModal = new ProjectModal();
  }
  
  // Handle both projects list links and featured project links
  const projectLinks = document.querySelectorAll(
    '.c-projects-list__item-link, .c-home-slideshow__link, .c-projects-selected__link, .c-projects-index__link, .c-projects-index-table__link'
  );
  
  if (projectLinks.length === 0) {
    return; // No project links found
  }
  
  projectLinks.forEach(function(link) {
    // Check if listener already attached
    if (link.dataset.modalListener === 'true') {
      return; // Already has listener
    }
    
    link.dataset.modalListener = 'true';
    link.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation(); // Prevent Swup from intercepting
      const url = this.getAttribute('href');
      if (projectModal && url) {
        projectModal.open(url, this);
      }
    }, true); // Use capture phase to run before Swup
  });
}

function initMobileNav() {
  const mobileNavToggle = document.querySelector(".js-nav-mobile-toggle");
  const mobileNav = document.querySelector(".c-nav-mobile");
  
  if (!mobileNavToggle || !mobileNav) return;
  
  // Remove old listener by cloning (clean way to remove all listeners)
  const newToggle = mobileNavToggle.cloneNode(true);
  mobileNavToggle.parentNode.replaceChild(newToggle, mobileNavToggle);
  
  // Get fresh reference after replacement
  const toggle = document.querySelector(".js-nav-mobile-toggle");
  const nav = document.querySelector(".c-nav-mobile");
  
  if (!toggle || !nav) return;
  
  // Attach toggle listener with fresh references
  toggle.addEventListener("click", function(e) {
    e.preventDefault();
    e.stopPropagation();
    // Always get fresh references inside handler
    const currentNav = document.querySelector(".c-nav-mobile");
    const currentToggle = document.querySelector(".js-nav-mobile-toggle");
    if (currentNav && currentToggle) {
      currentNav.classList.toggle("is-open");
      const isOpen = currentNav.classList.contains("is-open");
      currentToggle.setAttribute("aria-expanded", isOpen);
    }
  });
  
  // Attach listeners to nav links (these get replaced by Swup, so always reattach)
  const navLinks = document.querySelectorAll(".c-nav-mobile__link");
  navLinks.forEach(function(link) {
    link.addEventListener("click", function() {
      // Always get fresh references inside handler
      const currentNav = document.querySelector(".c-nav-mobile");
      const currentToggle = document.querySelector(".js-nav-mobile-toggle");
      if (currentNav && currentToggle) {
        currentNav.classList.remove("is-open");
        currentToggle.setAttribute("aria-expanded", "false");
      }
    });
  });
  
  // Close dropdown when clicking outside (only add once globally)
  if (!window.mobileNavOutsideClickHandler) {
    window.mobileNavOutsideClickHandler = function(e) {
      // Always get fresh references
      const currentNav = document.querySelector(".c-nav-mobile");
      const currentToggle = document.querySelector(".js-nav-mobile-toggle");
      if (currentNav && currentToggle && !currentNav.contains(e.target)) {
        currentNav.classList.remove("is-open");
        currentToggle.setAttribute("aria-expanded", "false");
      }
    };
    document.addEventListener("click", window.mobileNavOutsideClickHandler);
  }
}

function updateMobileNavLabel() {
  const mobileNavToggle = document.querySelector(".js-nav-mobile-toggle");
  if (!mobileNavToggle) return;
  
  const currentPath = window.location.pathname;
  const isHomepage = document.body.classList.contains("page-home");
  
  let labelText = "Menu";
  
  // First, try to find active link by aria-current
  let activeLink = document.querySelector(".c-nav-mobile__link[aria-current='page']");
  
  if (activeLink) {
    labelText = activeLink.textContent.trim();
  } else {
    // If no aria-current, try regular nav
    const regularActiveLink = document.querySelector(".c-nav-item a[aria-current='page']");
    if (regularActiveLink) {
      labelText = regularActiveLink.textContent.trim();
    } else if (!isHomepage) {
      // Match by URL - check mobile nav links first
      const allMobileLinks = document.querySelectorAll(".c-nav-mobile__link");
      let found = false;
      
      for (let link of allMobileLinks) {
        try {
          const linkPath = new URL(link.href).pathname;
          // Check for exact match or if current path starts with link path
          if (linkPath === currentPath || (linkPath !== '/' && currentPath.startsWith(linkPath))) {
            labelText = link.textContent.trim();
            found = true;
            break;
          }
        } catch (e) {
          continue;
        }
      }
      
      // If not found in mobile nav, check regular nav links
      if (!found) {
        const allRegularLinks = document.querySelectorAll(".c-nav-item a");
        for (let link of allRegularLinks) {
          try {
            const linkPath = new URL(link.href).pathname;
            if (linkPath === currentPath || (linkPath !== '/' && currentPath.startsWith(linkPath))) {
              labelText = link.textContent.trim();
              found = true;
              break;
            }
          } catch (e) {
            continue;
          }
        }
      }
      
      // Final fallback: try to get from page title
      if (!found && labelText === "Menu") {
        const pageTitle =
          document.querySelector("h1.u-sr-only") ||
          document.querySelector(".c-filters__heading-text");
        if (pageTitle) {
          labelText = pageTitle.textContent.trim();
        }
      }
    }
  }
  
  // Update the button text only if we have a valid label
  if (labelText && labelText !== "Menu" || isHomepage) {
    mobileNavToggle.textContent = labelText;
  }
}

function initFilters() {
  const filtersToggle = document.querySelector(".js-filters-toggle");
  const filters = document.querySelector(".c-filters");
  const filterLinks = document.querySelectorAll(".c-filters .js-filter-link, .c-filters__dropdown .js-filter-link");
  const projectItems = document.querySelectorAll(".js-item-to-filter");

  if (document.querySelector(".c-projects-filters")) {
    return;
  }
  
  if (filtersToggle && filters) {
    filtersToggle.addEventListener("click", function(e) {
      e.preventDefault();
      e.stopPropagation();
      filters.classList.toggle("is-open");
      const isOpen = filters.classList.contains("is-open");
      filtersToggle.setAttribute("aria-expanded", isOpen);
    });
    
    // Close dropdown when clicking outside
    document.addEventListener("click", function(e) {
      if (!filters.contains(e.target)) {
        filters.classList.remove("is-open");
        filtersToggle.setAttribute("aria-expanded", "false");
      }
    });
  }
  
  // Helper function to slugify a string
  function slugify(text) {
    return text.toString().toLowerCase()
      .replace(/\s+/g, '-')           // Replace spaces with -
      .replace(/[^\w\-]+/g, '')       // Remove all non-word chars
      .replace(/\-\-+/g, '-')         // Replace multiple - with single -
      .replace(/^-+/, '')             // Trim - from start of text
      .replace(/-+$/, '');            // Trim - from end of text
  }
  
  // Track selected filters
  let selectedFilters = new Set();
  
  // Function to update filter display
  function updateFilterDisplay() {
    projectItems.forEach(function(item) {
      const typology = item.getAttribute("data-typology") || "";
      
      if (selectedFilters.size === 0) {
        // Show all projects when no filters selected
        item.classList.remove("is-hidden");
        item.style.display = "";
      } else {
        // Typology might be comma-separated, convert each to slug and check
        const typologyValues = typology.split(',').map(function(t) {
          return slugify(t.trim());
        });
        
        // Check if item matches any selected filter
        const matchesFilter = Array.from(selectedFilters).some(function(filterValue) {
          return typologyValues.includes(filterValue);
        });
        
        if (matchesFilter) {
          item.classList.remove("is-hidden");
          item.style.display = "";
        } else {
          item.classList.add("is-hidden");
          item.style.display = "none";
        }
      }
    });
  }
  
  // Filter projects when clicking a filter link
  if (filterLinks.length > 0 && projectItems.length > 0) {
    filterLinks.forEach(function(link) {
      link.addEventListener("click", function(e) {
        e.preventDefault();
        e.stopPropagation();
        
        // Get filter value (slug)
        const filterValue = this.getAttribute("data-filter");
        
        // Toggle filter selection
        if (this.classList.contains("is-active")) {
          // Deselect: remove from active filters
          this.classList.remove("is-active");
          selectedFilters.delete(filterValue);
        } else {
          // Select: add to active filters
          this.classList.add("is-active");
          selectedFilters.add(filterValue);
        }
        
        // Update filter display
        updateFilterDisplay();
        
        // Keep dropdown open - don't close it
      });
    });
    
    // Apply initial filter if URL has a category parameter
    const urlParams = new URLSearchParams(window.location.search);
    const categoryParam = urlParams.get('categories');
    if (categoryParam) {
      const categorySlugs = categoryParam.split(',').map(function(c) {
        return c.trim();
      });
      categorySlugs.forEach(function(slug) {
        const matchingLink = Array.from(filterLinks).find(function(link) {
          return link.getAttribute("data-filter") === slug;
        });
        if (matchingLink) {
          matchingLink.classList.add("is-active");
          selectedFilters.add(slug);
        }
      });
      updateFilterDisplay();
    }
  }
}

function init() {
  if (history.scrollRestoration) {
    history.scrollRestoration = "manual";
  }

  if (isArticlePage) {
    const slug = window.location.pathname.split("/").filter(Boolean).pop();
    window.location.replace(newsIndexUrl(slug || ""));
    return;
  }

  // Check if we should open a project modal after navigation
  // (e.g. legacy session flag — modal open from projects index is primary path)
  const projectUrlToOpen = sessionStorage.getItem('openProjectModal');
  if (projectUrlToOpen && isProjectsPage) {
    sessionStorage.removeItem('openProjectModal');
    requestAnimationFrame(() => {
      setTimeout(() => {
        openStoredProjectModal(projectUrlToOpen);
      }, 300);
    });
  }

  const introelement = document.getElementById("intro");
  if (typeof introelement != "undefined" && introelement != null && typeof intro === "function") {
    intro(introRan, isHomepage);
    introRan = true;
  }

  initSortableList();
  toggleView();
  initProjectsView();
  collapsibleText();
  initFilters();
  initMobileNav();
  initMobileNavMenu();
  initLangSwitcher();
  initHeaderContact();
  refreshMobileNav();
  updateMobileNavLabel();
  swupActiveLinks();
  initCarousel();
  initProjectLinks(); // Always try to initialize project links if they exist
  // Homepage logo intro + rest state are owned by initHomepageExperience —
  // calling initMaLogoScroll here would start the intro then immediately
  // cancel it (cleanup marks intro done for this page life).
  if (!isHomepage) {
    initMaLogoScroll();
  }
  initSal();
  if (!isTouchDevice) {
    floatingImages();
  }
  
  if (isHomepage) {
    initHomepageExperience();
  }

  if (isProjectPage) {
    const lb = new Lightbox({
      selector: '#main a[data-rel="aiLightbox"]', // exclude modal (own Lightbox)
      lazyload: true, // boolean
      arrows: true, // boolean
      counter: true, // boolean
      slideSpeed: 500, //number(ms)
    });
  }

  if (isNewsPage) {
    initNewsAccordion();
    initNewsLoadMore();
    initNewsYears();
  }

  if (isProjectsPage) {
    if (!projectModal) {
      projectModal = new ProjectModal();
    }
    initProjectLinks();
  }

  if (isAboutPage) {
    initAboutCarousel();
    initAboutTeam();
    initAboutNav();
  }

  function setOffsetPosition() {
    const logoElement = document.querySelector(".js-logo-part");
    if (logoElement) {
      const logoStyles = getComputedStyle(logoElement);
      const logoHeight = logoElement.offsetHeight + parseFloat(logoStyles.marginTop) + parseFloat(logoStyles.marginBottom);
      //Set 'top' of sticky element to the offsetheight of logo
      const elements = document.querySelectorAll(".js-stick-offset-logo");
      elements.forEach((element) => {
        element.style.top = logoHeight + "px";
      });
    }
  }

  function fadeOnScroll() {
    const logoElement = document.querySelector(".js-logo-part");
    if (logoElement) {
      const logoHeight = document.querySelector(".js-logo-part").offsetHeight;
      const elements = document.querySelectorAll(".js-fade-on-scroll");
      elements.forEach((element) => {
        const scrollPosition = window.scrollY;
        const opacity = 1 - scrollPosition / (logoHeight / 1.5);
        element.style.opacity = opacity;
      });
    }
  }

  setOffsetPosition();
  window.addEventListener("resize", setOffsetPosition);
  window.addEventListener("scroll", fadeOnScroll);

  function projectNav() {
    // Title is now in modal header (fixed position), no scroll-based opacity needed
  }

  if (isProjectPage) {
    projectNav();
  }

  const projectTitles = document.querySelectorAll('.c-project-modal__title');
  projectTitles.forEach((titleEl) => {
    const text = titleEl.textContent?.trim() || '';
    titleEl.classList.toggle('is-long', text.length > 20);
  });

  applyReducedMotionVideos();
  if (isHomepage) {
    initFeaturedCoverMedia(document.getElementById("main"));
  } else {
    initLazyMedia(document.getElementById("main"));
  }
}

// Ensure DOM is ready before initializing
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    checks();
    setupPlugins();
    init();
  });
} else {
  // DOM is already ready
  checks();
  setupPlugins();
  init();
}
